import { useState, useEffect } from 'react';
import { apiClient, LONG_REQUEST_TIMEOUT_MS } from '../../services/http.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useTheme } from '../../hooks/useTheme.jsx';
import { usePreferences } from '../../hooks/usePreferences.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import OnboardingModal from '../Onboarding/OnboardingModal.jsx';
import { useConfirm } from '../../hooks/useConfirm.jsx';
import { useModalA11y } from '../../hooks/useModalA11y.js';
import { isNative, isIOS } from '../../services/platform.js';

const TABS = [
    { id: 'general', label: 'Geral', icon: 'person' },
    { id: 'account', label: 'Conta', icon: 'manage_accounts' },
    { id: 'preferences', label: 'Preferências', icon: 'tune' },
    { id: 'privacy', label: 'Privacidade', icon: 'security' },
];

const PLAN_LABELS = {
    trial: 'Trial (Período de Teste)',
    basic: 'Plano Básico',
    regular: 'Plano Regular',
    premium: 'Plano Premium',
};

const STATUS_LABELS = { paid: 'Pago', open: 'Em aberto', void: 'Cancelado', uncollectible: 'Inadimplente' };

export const SettingsModal = ({ isOpen, onClose, availableCredits = 0 }) => {
    const { logout, user, refreshSession } = useAuth();
    const { theme, setTheme } = useTheme();
    const { showToast, showApiErrorToast } = useToast();
    const confirm = useConfirm();
    const { preferences, updatePreferences } = usePreferences();
    const [activeTab, setActiveTab] = useState('general');
    // No mobile a navegação é em duas etapas (lista de seções → detalhe em
    // tela cheia com botão "voltar"), como as Configurações do iPhone. No
    // desktop as duas colunas continuam sempre visíveis lado a lado, então
    // este estado é ignorado a partir de md.
    const [mobileShowList, setMobileShowList] = useState(true);
    const [billingData, setBillingData] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [showOnboardingEdit, setShowOnboardingEdit] = useState(false);
    const isPremium = billingData?.plan === 'premium';
    const [isCancelling, setIsCancelling] = useState(false);
    // Assinatura feita na App Store / Google Play: só a loja cancela (o backend recusa aqui).
    const storeManaged = billingData?.provider === 'apple' || billingData?.provider === 'google';
    const modalA11y = useModalA11y(isOpen, onClose, 'settings-title');

    const [profileData, setProfileData] = useState({
        full_name: user?.name || '',
        birth_date: user?.birth_date || '',
        gender: user?.gender || ''
    });
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [profileError, setProfileError] = useState('');

    useEffect(() => {
        if (user) {
            setProfileData({
                full_name: user.name || '',
                birth_date: user.birth_date ? user.birth_date.split('T')[0] : '',
                gender: user.gender || ''
            });
        }
    }, [user]);

    const handleSaveProfile = async () => {
        setProfileError('');

        if (profileData.birth_date) {
            const birthDate = new Date(profileData.birth_date);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            if (age < 16) {
                setProfileError('Você precisa ter pelo menos 16 anos para usar o CatholicGPT.');
                return;
            }
        }

        setIsSavingProfile(true);
        try {
            const { res, data } = await apiClient.request('/auth/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData),
            });

            if (res.ok) {
                showToast({ type: 'success', message: 'Perfil atualizado com sucesso!' });
                refreshSession();
            } else {
                showApiErrorToast(res, data, 'Erro ao atualizar perfil.');
            }
        } catch {
            showApiErrorToast(null, null, 'Erro ao conectar ao servidor.');
        } finally {
            setIsSavingProfile(false);
        }
    };

    // Preferences are now managed by usePreferences hook

    useEffect(() => {
        if (isOpen) setMobileShowList(true);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        apiClient.request('/billing/credits').then(({ res, data }) => {
            if (res.ok) setBillingData(data);
        });
    }, [isOpen]);

    useEffect(() => {
        if (activeTab !== 'account') return;
        apiClient.request('/billing/invoices').then(({ res, data }) => {
            if (res.ok) setInvoices(data.invoices ?? []);
        });
    }, [activeTab]);

    // Usuário perdeu o Premium (ex: downgrade) mas ainda está no tema exclusivo — reverte para escuro
    useEffect(() => {
        if (billingData && theme === 'contemplativo' && !isPremium) {
            setTheme('dark');
        }
    }, [billingData, isPremium, theme]);

    const handleReactivateSubscription = async () => {
        try {
            const { res } = await apiClient.request('/billing/subscriptions/reactivate', {
                method: 'POST'
            });
            if (res.ok) {
                showToast({ type: 'success', message: 'Assinatura reativada com sucesso!' });
                const { res: res2, data } = await apiClient.request('/billing/credits');
                if (res2.ok) setBillingData(data);
            } else {
                showToast({ type: 'error', message: 'Erro ao reativar assinatura' });
            }
        } catch {
            showToast({ type: 'error', message: 'Erro de conexão' });
        }
    };

    const handleManageStore = async () => {
        const isApple = billingData?.provider === 'apple';
        if (isNative()) {
            // Sai do app direto para o painel de assinaturas do sistema.
            const url = isIOS()
                ? 'itms-apps://apps.apple.com/account/subscriptions'
                : 'https://play.google.com/store/account/subscriptions';
            try {
                const { App } = await import('@capacitor/app');
                await App.openUrl({ url });
                return;
            } catch {
                // cai para o navegador
            }
        }
        window.open(
            isApple
                ? 'https://apps.apple.com/account/subscriptions'
                : 'https://play.google.com/store/account/subscriptions',
            '_blank',
            'noopener'
        );
    };

    const handleCancelSubscription = async () => {
        const planLabel = PLAN_LABELS[billingData?.plan] ?? 'atual';
        const until = billingData?.cancels_at
            ? new Date(billingData.cancels_at).toLocaleDateString('pt-BR')
            : 'o fim do período já pago';
        if (!(await confirm({
            title: 'Cancelar plano',
            message: `Você continua com o ${planLabel} e seus créditos até ${until}. Depois disso não haverá nova cobrança.\n\nVocê pode reativar antes dessa data.`,
            variant: 'danger',
            confirmText: 'Sim, cancelar plano',
            cancelText: 'Manter plano'
        }))) return;

        setIsCancelling(true);
        try {
            const { res, data } = await apiClient.request('/billing/subscriptions/cancel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ at_period_end: true }),
            });
            if (res.ok) {
                showToast({ type: 'success', message: 'Cancelamento agendado. Seu acesso segue até o fim do período.' });
                const { res: res2, data: fresh } = await apiClient.request('/billing/credits');
                if (res2.ok) setBillingData(fresh);
            } else {
                showToast({ type: 'error', message: (typeof data?.detail === 'string' && data.detail) || 'Não foi possível cancelar o plano.' });
            }
        } catch {
            showToast({ type: 'error', message: 'Erro de conexão' });
        } finally {
            setIsCancelling(false);
        }
    };

    if (!isOpen) return null;

    const handleDataExport = async () => {
        try {
            const { res, data } = await apiClient.request('/auth/data-export', {
                // Serializa todo o histórico do usuário — para contas antigas
                // isso passa fácil do timeout padrão de 15s.
                timeoutMs: LONG_REQUEST_TIMEOUT_MS,
            });
            if (!res.ok) throw new Error('export_failed');

            const dataStr = JSON.stringify(data, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'export.json';
            a.click();
        } catch {
            showToast({ type: 'error', message: 'Erro ao exportar dados' });
        }
    };

    const handleDeleteAccount = async () => {
        if (!(await confirm({
            title: 'Deletar Conta',
            message: 'Tem certeza? Sua conta será deletada em 30 dias.',
            variant: 'danger',
            confirmText: 'Sim, deletar'
        }))) return;

        try {
            const { res } = await apiClient.request('/auth/delete-account', { method: 'DELETE' });
            if (!res.ok) throw new Error('delete_failed');
            showToast({ type: 'success', message: 'Sua conta será deletada em 30 dias' });
            setTimeout(() => logout(), 2000);
        } catch {
            showToast({ type: 'error', message: 'Erro ao deletar conta' });
        }
    };

    const handleDeleteChatHistory = async () => {
        if (!(await confirm({
            title: 'Apagar Histórico de Mensagens',
            message: 'Tem certeza de que deseja apagar todo o seu histórico de mensagens?\n\nAs conversas continuarão existindo (com os títulos resetados), mas as mensagens serão removidas de forma definitiva. Esta ação não pode ser desfeita.',
            variant: 'danger',
            confirmText: 'Apagar'
        }))) return;

        try {
            const { res } = await apiClient.request('/auth/chat-history', { method: 'DELETE' });
            if (!res.ok) throw new Error('delete_history_failed');
            showToast({ type: 'success', message: 'Histórico de conversas apagado com sucesso!' });
            setTimeout(() => window.location.reload(), 1500);
        } catch {
            showToast({ type: 'error', message: 'Erro ao apagar histórico de conversas' });
        }
    };

    const handleDeleteConversations = async () => {
        if (!(await confirm({
            title: 'Apagar TODAS as Conversas',
            message: 'Tem certeza de que deseja apagar TODAS as suas conversas por completo?\n\nEsta ação remove as conversas da barra lateral, todo o histórico de mensagens e não pode ser desfeita.',
            variant: 'danger',
            confirmText: 'Apagar tudo'
        }))) return;

        try {
            const { res } = await apiClient.request('/auth/conversations', { method: 'DELETE' });
            if (!res.ok) throw new Error('delete_conversations_failed');
            showToast({ type: 'success', message: 'Conversas apagadas com sucesso!' });
            setTimeout(() => window.location.reload(), 1500);
        } catch {
            showToast({ type: 'error', message: 'Erro ao apagar conversas' });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 md:p-4">
            <div
                {...modalA11y}
                className="bg-surface-container w-full h-dvh md:h-[80vh] md:rounded-2xl md:max-w-4xl flex flex-col md:flex-row overflow-hidden md:shadow-2xl animate-fade-in md:border md:border-outline-variant/20 relative"
            >

                {/* Close Button Absolute — some sempre fecha o modal inteiro,
                    inclusive na tela de detalhe do mobile (o botão "voltar"
                    ali ao lado cuida só de voltar para a lista). */}
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-surface-variant/50 hover:bg-surface-variant text-on-surface-variant hover:text-on-surface transition-colors z-10"
                >
                    <span className="material-symbols-outlined text-[20px]">close</span>
                </button>

                {/* Lista de seções — ocupa a tela inteira no mobile (estilo
                    Configurações do iPhone/ChatGPT); no desktop é a coluna
                    fixa da esquerda, sempre visível ao lado do conteúdo. */}
                <div className={`${mobileShowList ? 'flex' : 'hidden'} md:flex w-full md:w-1/4 md:min-w-[240px] bg-surface-container-lowest md:border-r border-outline-variant/20 flex-col`}>
                    <div className="p-6 pb-4">
                        <h2 id="settings-title" className="text-xl font-bold font-serif mb-2 md:mb-6 text-on-surface">Configurações</h2>
                    </div>
                    <div className="flex-1 overflow-y-auto md:px-4 md:pb-6">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => { setActiveTab(tab.id); setMobileShowList(false); }}
                                className={`w-full flex items-center gap-3 px-6 py-3.5 md:px-3 md:py-2.5 md:mb-1 md:rounded-lg border-b border-outline-variant/10 md:border-none text-sm font-medium transition-all ${
                                    activeTab === tab.id
                                        ? 'text-on-surface md:bg-primary md:text-on-primary md:shadow-md md:divine-glow-active'
                                        : 'text-on-surface-variant hover:bg-surface-variant/60 hover:text-on-surface'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: activeTab === tab.id ? "'FILL' 1" : "'FILL' 0" }}>
                                    {tab.icon}
                                </span>
                                {tab.label}
                                <span className="material-symbols-outlined text-[18px] text-on-surface-variant/40 ml-auto md:hidden">chevron_right</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Area — em tela cheia no mobile, com botão "voltar"
                    para a lista; no desktop fica sempre ao lado da lista. */}
                <div className={`${mobileShowList ? 'hidden' : 'flex'} md:flex flex-1 flex-col min-w-0 bg-surface-container-low relative`}>
                    {/* Cabeçalho do Conteúdo */}
                    <div className="px-4 md:px-8 py-4 md:py-6 border-b border-outline-variant/10 flex items-center gap-2">
                        <button
                            onClick={() => setMobileShowList(true)}
                            aria-label="Voltar para a lista de configurações"
                            className="md:hidden -ml-1 w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-variant/50 text-on-surface"
                        >
                            <span className="material-symbols-outlined text-[22px]">arrow_back</span>
                        </button>
                        <h3 className="text-xl font-bold text-on-surface tracking-tight">
                            {TABS.find(t => t.id === activeTab)?.label}
                        </h3>
                    </div>

                    <div className="p-4 md:p-8 overflow-y-auto flex-1 custom-scrollbar">
                    {activeTab === 'general' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="min-w-0">
                                    <label className="block text-sm mb-1 text-on-surface-variant">Nome Completo</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 bg-surface border border-outline-variant rounded"
                                        placeholder="Opcional"
                                        value={profileData.full_name}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <label className="block text-sm mb-1 text-on-surface-variant">E-mail</label>
                                    <input
                                        type="email"
                                        className="w-full p-2 bg-surface-container-highest border border-outline-variant rounded text-on-surface-variant"
                                        value={user?.email || ''}
                                        readOnly
                                    />
                                    <p className="text-xs text-on-surface-variant mt-1">Para alterar o e-mail, entre em contato com o suporte.</p>
                                </div>
                                <div className="min-w-0">
                                    <label className="block text-sm mb-1 text-on-surface-variant">Sexo</label>
                                    <select
                                        className="w-full p-2 bg-surface border border-outline-variant rounded text-on-surface"
                                        value={profileData.gender}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, gender: e.target.value }))}
                                    >
                                        <option value="">Prefiro não informar</option>
                                        <option value="M">Masculino</option>
                                        <option value="F">Feminino</option>
                                    </select>
                                </div>
                                <div className="min-w-0">
                                    <label className="block text-sm mb-1 text-on-surface-variant">Data de Nascimento</label>
                                    <input
                                        type="date"
                                        className="w-full max-w-full min-w-0 p-2 bg-surface border border-outline-variant rounded box-border"
                                        value={profileData.birth_date}
                                        onChange={(e) => {
                                            setProfileError('');
                                            setProfileData(prev => ({ ...prev, birth_date: e.target.value }));
                                        }}
                                    />
                                    {profileError && <p className="text-xs text-error mt-1">{profileError}</p>}
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={isSavingProfile || (
                                        profileData.full_name === (user?.name || '') &&
                                        profileData.birth_date === (user?.birth_date ? user.birth_date.split('T')[0] : '') &&
                                        profileData.gender === (user?.gender || '')
                                    )}
                                    className="px-6 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
                                >
                                    {isSavingProfile ? 'Salvando...' : 'Salvar Alterações'}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'account' && (
                        <div className="space-y-6">
                            <div className="bg-surface p-4 rounded-lg border border-outline-variant flex justify-between items-center">
                                <div>
                                    <h3 className="font-bold text-lg">Plano Atual</h3>
                                    {billingData ? (
                                        <>
                                            <p className="text-on-surface-variant">
                                                {PLAN_LABELS[billingData.plan] ?? billingData.plan}
                                            </p>

                                        </>
                                    ) : (
                                        <p className="text-on-surface-variant">Carregando...</p>
                                    )}
                                </div>
                                <span className="bg-primary-container text-on-primary-container px-3 py-1 rounded-full text-sm font-bold">
                                    {billingData?.plan === 'trial' ? 'Trial' : (billingData?.cancel_at_period_end ? 'Cancelamento Agendado' : 'Ativo')}
                                </span>
                            </div>

                            {billingData?.cancel_at_period_end && (
                                <div className="bg-error-container text-on-error-container p-4 rounded-lg flex justify-between items-center">
                                    <p className="text-sm font-bold">
                                        {billingData.cancels_at ? `Sua assinatura será encerrada em ${new Date(billingData.cancels_at).toLocaleDateString('pt-BR')}` : 'Sua assinatura será encerrada ao fim do período atual.'}
                                    </p>
                                    <button
                                        onClick={storeManaged ? handleManageStore : handleReactivateSubscription}
                                        className="px-4 py-1.5 bg-on-error-container text-error-container rounded-full text-sm font-bold hover:opacity-90"
                                    >
                                        {storeManaged ? 'Gerenciar na loja' : 'Reativar'}
                                    </button>
                                </div>
                            )}

                            {billingData && billingData.plan !== 'trial' && !billingData.cancel_at_period_end && (
                                <div className="bg-surface p-4 rounded-lg border border-outline-variant flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                        <h3 className="font-bold text-lg">Assinatura</h3>
                                        <p className="text-sm text-on-surface-variant">
                                            {storeManaged
                                                ? `Cobrada pela ${billingData.provider === 'apple' ? 'App Store' : 'Google Play'}; o cancelamento é feito na própria loja.`
                                                : 'Você pode cancelar quando quiser e continua com acesso até o fim do período pago.'}
                                        </p>
                                    </div>
                                    {storeManaged ? (
                                        <button
                                            onClick={handleManageStore}
                                            className="px-4 py-2 border border-outline-variant text-on-surface rounded-full text-sm font-bold hover:bg-surface-variant/50 transition-colors whitespace-nowrap"
                                        >
                                            Gerenciar na loja
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleCancelSubscription}
                                            disabled={isCancelling}
                                            className="px-4 py-2 border border-error text-error rounded-full text-sm font-bold hover:bg-error/10 transition-colors disabled:opacity-50 whitespace-nowrap"
                                        >
                                            {isCancelling ? 'Cancelando...' : 'Cancelar plano'}
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="bg-surface p-4 rounded-lg border border-outline-variant">
                                <h3 className="font-bold text-lg mb-2">Créditos de Mensagens</h3>
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="flex-1 bg-surface-container-highest rounded-full h-4 overflow-hidden">
                                        <div
                                            className="bg-primary h-full"
                                            style={{ width: `${billingData ? (billingData.allocated === 0 ? 100 : Math.min(100, Math.round(((billingData.allocated - billingData.available) / billingData.allocated) * 100))) : 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-bold text-on-surface-variant whitespace-nowrap">
                                        {billingData ? billingData.available : availableCredits} restantes
                                    </span>
                                </div>
                                {billingData && (
                                    <p className="text-xs text-on-surface-variant">
                                        {!billingData.reset_date && billingData.plan === 'trial'
                                            ? 'Créditos do período de teste — não renovam automaticamente'
                                            : billingData.reset_date
                                                ? `Renova em ${new Date(billingData.reset_date).toLocaleDateString('pt-BR')}`
                                                : ''}
                                    </p>
                                )}
                            </div>

                            <div className="bg-surface p-4 rounded-lg border border-outline-variant">
                                <h3 className="font-bold text-lg mb-4">Histórico de Pagamentos</h3>
                                {invoices === null ? (
                                    <p className="text-sm text-on-surface-variant">Carregando...</p>
                                ) : invoices.length === 0 ? (
                                    <p className="text-sm text-on-surface-variant">Nenhum pagamento encontrado.</p>
                                ) : (
                                    <div className="space-y-0 divide-y divide-outline-variant">
                                        {invoices.map((inv) => (
                                            <div key={inv.id} className="flex justify-between items-center text-sm py-2">
                                                <div className="flex flex-col">
                                                    <span className="text-on-surface font-medium">
                                                        {inv.date ? new Date(inv.date).toLocaleDateString('pt-BR') : '—'}
                                                    </span>
                                                    <span className="text-xs text-on-surface-variant">{inv.number || inv.id}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-on-surface font-bold">
                                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: inv.currency || 'BRL' }).format((inv.amount ?? 0) / 100)}
                                                    </span>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                                        inv.status === 'paid'
                                                            ? 'bg-primary-container text-on-primary-container'
                                                            : 'bg-error-container text-on-error-container'
                                                    }`}>
                                                        {STATUS_LABELS[inv.status] ?? inv.status}
                                                    </span>
                                                    {inv.pdf_url && (
                                                        <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline">PDF</a>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="p-4 bg-surface rounded-lg border border-outline-variant flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-bold">Preferências de Boas-Vindas</h3>
                                    <p className="text-sm text-on-surface-variant">
                                        Objetivo, nível de familiaridade, tempo diário, profundidade das respostas e lembretes.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowOnboardingEdit(true)}
                                    className="px-4 py-2 bg-primary-container text-on-primary-container font-bold rounded whitespace-nowrap hover:opacity-90"
                                >
                                    Editar preferências
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'preferences' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            {/* Coluna esquerda: Voz · Velocidade · Entonação */}
                            <div className="space-y-6">
                                <div>
                                    <label className="block font-bold mb-2">Voz de Leitura</label>
                                    <select
                                        value={preferences.voice}
                                        onChange={(e) => updatePreferences({voice: e.target.value})}
                                        className="w-full p-2 bg-surface border border-outline-variant rounded"
                                    >
                                        <option value="feminina">Feminina (Padrão)</option>
                                        <option value="masculina">Masculina</option>
                                    </select>
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="font-bold">Velocidade</label>
                                        <span className="text-primary text-sm font-bold">{Number(preferences.speed).toFixed(1)}x</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0.5" max="2.0" step="0.1"
                                        value={preferences.speed}
                                        onChange={(e) => updatePreferences({speed: parseFloat(e.target.value)})}
                                        className="w-full accent-primary"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="font-bold">Entonação</label>
                                        <span className="text-primary text-sm font-bold">{Number(preferences.intonation).toFixed(1)}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0.5" max="2.0" step="0.1"
                                        value={preferences.intonation}
                                        onChange={(e) => updatePreferences({intonation: parseFloat(e.target.value)})}
                                        className="w-full accent-primary"
                                    />
                                </div>
                            </div>

                            {/* Coluna direita: Fonte · Idioma */}
                            <div className="space-y-6">
                                <div>
                                    <label className="block font-bold mb-2">Tamanho da Fonte (Chat)</label>
                                    <select
                                        value={preferences.fontSize}
                                        onChange={(e) => updatePreferences({fontSize: e.target.value})}
                                        className="w-full p-2 bg-surface border border-outline-variant rounded"
                                    >
                                        <option value="P">Pequena</option>
                                        <option value="M">Média</option>
                                        <option value="G">Grande</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block font-bold mb-2">Idioma do Chat</label>
                                    <select
                                        value={preferences.language}
                                        onChange={(e) => updatePreferences({language: e.target.value})}
                                        className="w-full p-2 bg-surface border border-outline-variant rounded"
                                    >
                                        <option value="pt-BR">Português (Brasil)</option>
                                        <option value="en-US">Inglês (US)</option>
                                        <option value="es-ES">Espanhol</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block font-bold mb-2">Tema Visual</label>
                                    <select
                                        value={theme}
                                        onChange={(e) => setTheme(e.target.value)}
                                        className="w-full p-2 bg-surface border border-outline-variant rounded"
                                    >
                                        <option value="dark">Escuro</option>
                                        <option value="light">Claro</option>
                                        <option value="contemplativo" disabled={!isPremium}>
                                            Contemplativo {isPremium ? '' : '(exclusivo Premium)'}
                                        </option>
                                    </select>
                                    {!isPremium && (
                                        <p className="text-xs text-on-surface-variant mt-1">
                                            O Modo Contemplativo é exclusivo para assinantes do Plano Premium.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'privacy' && (
                        <div className="space-y-4">
                            <div className="p-4 bg-surface rounded-lg border border-outline-variant flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-bold">Exportar Meus Dados</h3>
                                    <p className="text-sm text-on-surface-variant">Faça o download de todos os seus dados em formato JSON (LGPD).</p>
                                </div>
                                <button
                                    onClick={handleDataExport}
                                    className="px-4 py-2 bg-primary-container text-on-primary-container font-bold rounded whitespace-nowrap hover:opacity-90"
                                >
                                    📥 Exportar Dados
                                </button>
                            </div>

                            <div className="p-4 bg-surface rounded-lg border border-outline-variant flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-bold">Apagar Histórico de Mensagens</h3>
                                    <p className="text-sm text-on-surface-variant">Apaga as mensagens, mas mantém as conversas na barra lateral (títulos resetados).</p>
                                </div>
                                <button
                                    onClick={handleDeleteChatHistory}
                                    className="px-4 py-2 bg-red-800 text-white font-bold rounded whitespace-nowrap hover:bg-red-900 transition-colors"
                                >
                                    🗑️ Apagar Mensagens
                                </button>
                            </div>

                            <div className="p-4 bg-surface rounded-lg border border-outline-variant flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-bold">Apagar Conversas por Completo</h3>
                                    <p className="text-sm text-on-surface-variant">Remove completamente todas as conversas e suas mensagens de forma definitiva.</p>
                                </div>
                                <button
                                    onClick={handleDeleteConversations}
                                    className="px-4 py-2 bg-red-800 text-white font-bold rounded whitespace-nowrap hover:bg-red-900 transition-colors"
                                >
                                    🗑️ Apagar Conversas
                                </button>
                            </div>

                            <div className="p-4 bg-red-900 rounded-lg border border-red-950 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-bold text-white">Deletar Conta</h3>
                                    <p className="text-sm text-red-100">Esta ação é irreversível. Seus dados serão deletados em 30 dias.</p>
                                </div>
                                <button
                                    onClick={handleDeleteAccount}
                                    className="px-4 py-2 bg-white text-red-900 font-bold rounded whitespace-nowrap hover:bg-red-50 transition-colors"
                                >
                                    🗑️ Deletar Conta
                                </button>
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>
            {showOnboardingEdit && (
                <OnboardingModal
                    mode="edit"
                    initialPreferences={user?.onboarding_preferences || null}
                    onClose={() => setShowOnboardingEdit(false)}
                />
            )}
        </div>
    );
};
